import React, { useEffect, useState } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { publicOrigin } from "@/lib/publicOrigin";
import { useApp } from "@/context/AppContext";
import { useNavigate, Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveMediaUrl } from "@/components/FileUpload";
import { CalendarClock, Bell, BellRing, Trash2, Radio, Share2, CalendarPlus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { SharePoster } from "@/components/SharePoster";
import { localeFor, countdownInfo } from "@/lib/dateLocale";
import { avatarSrc } from "@/lib/defaults";

export const fmt = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(localeFor(lang), { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// Build a "Add to Google Calendar" link (1h default event, links back to the waiting room).
export const gcalUrl = (s) => {
  const pad = (n) => String(n).padStart(2, "0");
  const g = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
  const start = new Date(s.scheduled_at);
  const end = new Date(start.getTime() + 60 * 60000);
  const url = `${publicOrigin()}/live/waiting/${s.id}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Direct Yalola : ${s.title}`,
    dates: `${g(start)}/${g(end)}`,
    details: `${s.description ? s.description + "\n\n" : ""}Rejoins le direct de ${s.host?.name || ""} : ${url}`,
    location: url,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Live "starts in ..." countdown label (i18n via shared events.cd_* keys).
export const countdown = (iso, now, t = (k, f) => f) => {
  const diff = new Date(iso).getTime() - now;
  if (diff <= 60000) return t("events.cd_soon", "Commence bientôt");
  const min = Math.floor(diff / 60000);
  if (min < 60) return t("events.cd_mins", "Dans {n} min").replace("{n}", min);
  const h = Math.floor(min / 60);
  if (h < 24) return t("events.cd_hours", "Dans {n} h").replace("{n}", h);
  return t("events.cd_days", "Dans {n} j").replace("{n}", Math.floor(h / 24));
};

// Shared data + actions for scheduled lives (used by the feed carousel AND the dedicated page).
export const useScheduledLives = () => {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/live/scheduled")
    .then(({ data }) => setItems(data.scheduled || []))
    .catch(() => {})
    .finally(() => setLoading(false));
  useEffect(() => whenFeedReady(load), []);
  useEffect(() => {
    const stop = pausableInterval(() => setNow(Date.now()), 30000); // lot 699
    return () => stop();
  }, []);

  const cancel = async (id) => {
    try {
      await api.delete(`/live/scheduled/${id}`);
      toast.success(t("live.sched_cancelled", "Programmation annulée"));
      setItems((a) => a.filter((x) => x.id !== id));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const toggleRemind = async (id) => {
    try {
      const { data } = await api.post(`/live/scheduled/${id}/remind`);
      setItems((a) => a.map((x) => (x.id === id ? { ...x, reminded: data.reminded } : x)));
      toast.success(data.reminded ? t("live.remind_on", "Rappel activé — on vous préviendra 🔔") : t("live.remind_off", "Rappel désactivé"));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  return { items, now, loading, cancel, toggleRemind, reload: load };
};

// One scheduled-live card. `variant` = "carousel" (fixed width, cover on top) | "list" (full width).
export const ScheduledLiveCard = ({ s, now, onRemind, onCancel, onShare, variant = "carousel" }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const soon = new Date(s.scheduled_at).getTime() - now <= 30 * 60000;
  const wrap = variant === "carousel"
    ? "snap-start shrink-0 w-[78vw] max-w-[300px] rounded-2xl overflow-hidden border"
    : "w-full rounded-2xl overflow-hidden border";

  return (
    <div className={wrap} style={{ background: "var(--yalola-surface-alt)", borderColor: "var(--yalola-border)" }} data-testid={`scheduled-live-${s.id}`}>
      <button onClick={() => nav(`/live/waiting/${s.id}`)} className="relative block w-full text-left" data-testid={`scheduled-cover-${s.id}`}>
        {s.thumbnail_url ? (
          <img src={resolveMediaUrl(s.thumbnail_url)} alt={s.title} className="w-full object-cover" style={{ aspectRatio: "16 / 9" }} />
        ) : (
          <div className="w-full grid place-items-center" style={{ aspectRatio: "16 / 9", background: "linear-gradient(135deg, var(--yalola-primary), var(--yalola-secondary))" }}>
            <Radio size={28} className="text-white/80" />
          </div>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.05))" }} />
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 text-white" style={{ background: "var(--yalola-primary)" }}>
          <Radio size={10} /> {t("live.scheduled", "Programmé")}
        </span>
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 text-white" style={{ background: soon ? "var(--yalola-primary)" : "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }} data-testid={`scheduled-cover-countdown-${s.id}`}>
          <CalendarClock size={11} /> {countdown(s.scheduled_at, now, t)}
        </span>
        <div className="absolute bottom-2 left-3 right-24 text-left">
          <div className="text-sm font-bold text-white line-clamp-1" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>{s.title}</div>
          <div className="text-[11px] text-white/80 line-clamp-1">{fmt(s.scheduled_at, lang)}</div>
        </div>
      </button>

      <div className="flex items-center gap-2 px-2.5 py-2">
        <button onClick={() => nav(`/profile/${s.host?.id}`)} className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`scheduled-host-${s.id}`}>
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={avatarSrc(s.host)} />
            <AvatarFallback>{s.host?.name?.[0]}</AvatarFallback>
          </Avatar>
          <span className="text-xs font-semibold truncate" style={{ color: "var(--yalola-text-muted)" }}>{s.host?.name}</span>
        </button>

        <a
          href={gcalUrl(s)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="h-8 w-8 hidden sm:grid place-items-center rounded-full shrink-0 transition-colors hover:bg-black/5"
          style={{ color: "var(--yalola-primary)" }}
          title={t("live.add_calendar", "Ajouter au calendrier")}
          data-testid={`scheduled-calendar-${s.id}`}
        >
          <CalendarPlus size={15} />
        </a>
        <button
          onClick={() => onShare?.(s)}
          className="h-8 w-8 grid place-items-center rounded-full shrink-0 transition-colors hover:bg-black/5"
          style={{ color: "var(--yalola-primary)" }}
          title={t("live.share_countdown", "Partager le compte à rebours")}
          data-testid={`scheduled-share-${s.id}`}
        >
          <Share2 size={15} />
        </button>

        {s.is_mine ? (
          <>
            <button onClick={() => nav(`/live?scheduled=${s.id}`)} className="h-8 w-8 grid place-items-center rounded-full shrink-0 transition-colors hover:bg-black/5" style={{ color: "var(--yalola-primary)" }} title={t("live.open_studio", "Ouvrir le studio (clés OBS)")} data-testid={`scheduled-studio-${s.id}`}>
              <Radio size={15} />
            </button>
            <button onClick={() => onCancel?.(s.id)} className="h-8 w-8 grid place-items-center rounded-full text-red-500 hover:bg-red-50 transition-colors shrink-0" data-testid={`scheduled-cancel-${s.id}`}>
              <Trash2 size={15} />
            </button>
          </>
        ) : s.is_following ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid={`scheduled-reminder-${s.id}`}>
            <BellRing size={12} /> {t("live.reminder_on_short", "Rappel activé")}
          </span>
        ) : (
          <button
            onClick={() => onRemind?.(s.id)}
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 shrink-0 transition-colors border"
            style={s.reminded
              ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" }
              : { background: "transparent", color: "var(--yalola-primary)", borderColor: "var(--yalola-primary)" }}
            data-testid={`scheduled-remind-${s.id}`}
          >
            {s.reminded ? <BellRing size={12} /> : <Bell size={12} />} {s.reminded ? t("live.reminder_on_short", "Rappel activé") : t("live.remind_me", "M'inscrire")}
          </button>
        )}
      </div>
    </div>
  );
};

// Feed widget: horizontal carousel of the next 5 scheduled lives + "Voir tout" → dedicated page.
export const ScheduledLives = () => {
  const { t } = useApp();
  const { items, now, cancel, toggleRemind } = useScheduledLives();
  const [poster, setPoster] = useState(null);
  const MAX = 5;

  if (!items.length) return null;
  const shown = items.slice(0, MAX);
  const hasMore = items.length > MAX;

  return (
    <div className="yalola-card p-3.5" data-testid="scheduled-lives">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} style={{ color: "var(--yalola-primary)" }} />
          <span className="text-sm font-bold">{t("live.scheduled_title", "Directs programmés")}</span>
        </div>
        {hasMore && (
          <Link to="/lives/scheduled" className="inline-flex items-center gap-0.5 text-xs font-semibold shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid="scheduled-see-all">
            {t("live.see_all", "Voir tout")} ({items.length}) <ChevronRight size={14} />
          </Link>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1.5 -mx-1 px-1 snap-x hide-scrollbar" data-testid="scheduled-carousel">
        {shown.map((s) => (
          <ScheduledLiveCard key={s.id} s={s} now={now} onRemind={toggleRemind} onCancel={cancel} onShare={setPoster} variant="carousel" />
        ))}
        {hasMore && (
          <Link
            to="/lives/scheduled"
            className="snap-start shrink-0 w-[140px] rounded-2xl border grid place-items-center text-center gap-2 py-6 transition-colors hover:bg-black/5"
            style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-primary)" }}
            data-testid="scheduled-see-all-tile"
          >
            <span className="h-11 w-11 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><ChevronRight size={20} /></span>
            <span className="text-xs font-semibold">{t("live.see_all", "Voir tout")}<br />({items.length})</span>
          </Link>
        )}
      </div>
      {poster && (
        <SharePoster
          open={!!poster}
          onOpenChange={(v) => { if (!v) setPoster(null); }}
          title={poster.title}
          dateISO={poster.scheduled_at}
          hostName={poster.host?.name}
          shareUrl={`${publicOrigin()}/live/waiting/${poster.id}`}
        />
      )}
    </div>
  );
};

export default ScheduledLives;
